import React, { Suspense, useEffect, lazy } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { AdminProvider } from './contexts/AdminContext';
import { BlogProvider } from './contexts/BlogContext';
import { UserProvider } from './contexts/UserContext';
import { PageProvider } from './contexts/PageContext';
import { ToastContainer } from 'react-toastify';
import { getCachedData, init, startAutoCleanup } from './services/cacheService';

// React-Quill iframe selector sorununu çözmek için global stil
import './quillFix.css';

// Temel bileşenler
import Layout from './components/Layout';
import RequireAuth from './components/RequireAuth';

// Yükleme göstergesi
const LoadingSpinner = () => (
  <div className="flex items-center justify-center w-full h-screen">
    <div className="w-16 h-16 border-4 border-gray-300 rounded-full border-t-purple-600 animate-spin"></div>
  </div>
);

// Admin layout
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));

// Temel sayfalar
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/admin/Login'));

// Genel sayfalar - Grup olarak yüklenir
const Blog = lazy(() => import(/* webpackChunkName: "public-pages" */ './pages/Blog'));
const BlogDetail = lazy(() => import(/* webpackChunkName: "public-pages" */ './pages/BlogDetail'));
const Contact = lazy(() => import(/* webpackChunkName: "public-pages" */ './pages/Contact'));
const WhoWeAre = lazy(() => import(/* webpackChunkName: "public-pages" */ './pages/WhoWeAre'));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "public-pages" */ './pages/PrivacyPolicy'));
const PersonalDataDeclaration = lazy(() => import(/* webpackChunkName: "public-pages" */ './pages/PersonalDataDeclaration'));
const JobSearch = lazy(() => import(/* webpackChunkName: "public-pages" */ './pages/JobSearch'));
const JobDetail = lazy(() => import(/* webpackChunkName: "public-pages" */ './pages/JobDetail'));
const TalentTalks = lazy(() => import(/* webpackChunkName: "public-pages" */ './pages/TalentTalks'));
const ResourcesIndex = lazy(() => import(/* webpackChunkName: "public-pages" */ './pages/Resources'));

// Servis sayfaları - Grup olarak yüklenir
const ServicesIndex = lazy(() => import(/* webpackChunkName: "services-pages" */ './pages/Services'));
const CareerConsulting = lazy(() => import(/* webpackChunkName: "services-pages" */ './pages/Services/CareerConsulting'));
const Headhunting = lazy(() => import(/* webpackChunkName: "services-pages" */ './pages/Services/Headhunting'));
const PersonalityInventory = lazy(() => import(/* webpackChunkName: "services-pages" */ './pages/Services/PersonalityInventory'));
const Recruitment = lazy(() => import(/* webpackChunkName: "services-pages" */ './pages/Services/Recruitment'));
const HRPartnership = lazy(() => import(/* webpackChunkName: "services-pages" */ './pages/Services/HRPartnership'));
const TalentConnect = lazy(() => import(/* webpackChunkName: "services-pages" */ './pages/Services/HRPartnership/TalentConnect'));
const HRProject = lazy(() => import(/* webpackChunkName: "services-pages" */ './pages/Services/HRPartnership/HRProject'));
const TalentMapping = lazy(() => import(/* webpackChunkName: "services-pages" */ './pages/Services/HRPartnership/TalentMapping'));

// Admin sayfaları - Grup olarak yüklenir
const AdminPages = lazy(() => import(/* webpackChunkName: "admin-pages" */ './pages/admin/AdminPages'));
const AdminUsers = lazy(() => import(/* webpackChunkName: "admin-pages" */ './pages/admin/Users'));
const AdminJobs = lazy(() => import(/* webpackChunkName: "admin-pages" */ './pages/admin/Jobs'));
const AdminMediaLibrary = lazy(() => import(/* webpackChunkName: "admin-pages" */ './pages/admin/MediaLibrary'));
const AdminTalentTalks = lazy(() => import(/* webpackChunkName: "admin-pages" */ './pages/admin/TalentTalks'));
const AdminBlogList = lazy(() => import(/* webpackChunkName: "admin-pages" */ './pages/admin/BlogList'));
const AdminBlogEdit = lazy(() => import(/* webpackChunkName: "admin-pages" */ './pages/admin/BlogEdit'));
const AdminBlogForm = lazy(() => import(/* webpackChunkName: "admin-pages" */ './pages/admin/BlogForm'));
const AdminJobApplications = lazy(() => import(/* webpackChunkName: "admin-pages" */ './pages/admin/JobApplications'));
const AdminCVApplications = lazy(() => import(/* webpackChunkName: "admin-pages" */ './pages/admin/CVApplications'));
const AdminTalentApplications = lazy(() => import(/* webpackChunkName: "admin-pages" */ './pages/admin/TalentApplications'));

// Admin Dashboard Layout
const AdminDashboardLayout = () => (
  <RequireAuth>
    <AdminLayout>
      <Outlet />
    </AdminLayout>
  </RequireAuth>
);

import ICP from './components/WhyUs/ICP'
import CreatePersona from './components/WhyUs/CreatePersona'
import BestPractice from './components/WhyUs/BestPractice'

const App = () => {
  // Uygulama başlatma
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Önbellek servisini başlat
        init();
        startAutoCleanup();
      } catch (error) {
        console.error('Uygulama başlatılırken hata oluştu:', error);
      }
    };

    initializeApp();
  }, []);

  return (
    <HelmetProvider>
      <AdminProvider>
        <UserProvider>
          <BlogProvider>
            <PageProvider>
              <Helmet>
                <title>Talent Finder</title>
                <meta name="description" content="Connecting Talent with Opportunity" />
              </Helmet>
              
              <Toaster position="top-right" />
              <ToastContainer position="top-right" autoClose={3000} />
              
              <Routes>
                {/* Ana sayfa */}
                <Route path="/" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Home />
                  </Suspense>
                } />
                
                {/* Genel sayfalar */}
                <Route path="/" element={<Layout />}>
                  <Route path="who-we-are" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <WhoWeAre />
                    </Suspense>
                  } />
                  
                  <Route path="contact" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <Contact />
                    </Suspense>
                  } />
                  
                  <Route path="privacy-policy" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <PrivacyPolicy />
                    </Suspense>
                  } />
                  
                  <Route path="personal-data-declaration" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <PersonalDataDeclaration />
                    </Suspense>
                  } />
                  
                  {/* Blog sayfaları */}
                  <Route path="resources">
                    <Route index element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <ResourcesIndex />
                      </Suspense>
                    } />
                    <Route path="blog" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <Blog />
                      </Suspense>
                    } />
                    <Route path="blog/:slug" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <BlogDetail />
                      </Suspense>
                    } />
                    <Route path="talent-talks" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <TalentTalks />
                      </Suspense>
                    } />
                  </Route>
                  
                  {/* İş arama sayfaları */}
                  <Route path="for-talents">
                    <Route path="opportunities" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <JobSearch />
                      </Suspense>
                    } />
                    <Route path="opportunities/:id" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <JobDetail />
                      </Suspense>
                    } />
                  </Route>
                  
                  {/* Servis sayfaları */}
                  <Route path="for-partners">
                    <Route index element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <ServicesIndex />
                      </Suspense>
                    } />
                    <Route path="recruitment" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <Recruitment />
                      </Suspense>
                    } />
                    <Route path="headhunting" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <Headhunting />
                      </Suspense>
                    } />
                    <Route path="career-consulting" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <CareerConsulting />
                      </Suspense>
                    } />
                    <Route path="personality-inventory" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <PersonalityInventory />
                      </Suspense>
                    } />
                    <Route path="hr-partnership" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <HRPartnership />
                      </Suspense>
                    } />
                    <Route path="hr-partnership/talent-connect" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <TalentConnect />
                      </Suspense>
                    } />
                    <Route path="hr-partnership/hr-project" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <HRProject />
                      </Suspense>
                    } />
                    <Route path="hr-partnership/talent-mapping" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <TalentMapping />
                      </Suspense>
                    } />
                  </Route>
                </Route>
                
                {/* Admin sayfaları */}
                <Route path="/admin/login" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Login />
                  </Suspense>
                } />
                
                <Route path="/admin" element={<AdminDashboardLayout />}>
                  <Route index element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="dashboard" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AdminPages />
                    </Suspense>
                  } />
                  <Route path="users" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AdminUsers />
                    </Suspense>
                  } />
                  <Route path="jobs" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AdminJobs />
                    </Suspense>
                  } />
                  <Route path="pages" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AdminPages />
                    </Suspense>
                  } />
                  <Route path="media" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AdminMediaLibrary />
                    </Suspense>
                  } />
                  <Route path="talent-talks" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AdminTalentTalks />
                    </Suspense>
                  } />
                  <Route path="blog" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AdminBlogList />
                    </Suspense>
                  } />
                  <Route path="blog/new" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AdminBlogForm />
                    </Suspense>
                  } />
                  <Route path="blog/:id" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AdminBlogEdit />
                    </Suspense>
                  } />
                  <Route path="job-applications" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AdminJobApplications />
                    </Suspense>
                  } />
                  <Route path="cv-applications" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AdminCVApplications />
                    </Suspense>
                  } />
                  <Route path="talent-applications" element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AdminTalentApplications />
                    </Suspense>
                  } />
                </Route>
                
                {/* 404 sayfası */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </PageProvider>
          </BlogProvider>
        </UserProvider>
      </AdminProvider>
    </HelmetProvider>
  );
};

export default App;